import React, { useMemo, useState } from 'react';
import { tickerAPI } from '../../services/api';
import {
    toStr,
    lineupPlayerKey,
    rosterEntryKey,
    getLineupPlayerProfile,
    playerDisplayNameFromEntry,
    getOnFieldPlayers,
    getBenchPlayers,
    getSubbedOutIds,
    getSubbedInIds,
    getTickerEventPlayerKey,
    getTickerEventPlayerOutKey,
    getTickerEventPlayerInKey,
    buildTickerPlayerPayload,
    buildTickerSubPayload,
    applySubstitutionToLineup,
    revertSubstitutionFromLineup,
} from '../../utils/tickerLineupUtils';

const STAT_TYPES = new Set([2, 3, 4, 5]);

function PlayerChip({ entry, selected, onSelect, getNum }) {
    const key = lineupPlayerKey(entry);
    const profile = getLineupPlayerProfile(entry);
    const num = getNum(key) || entry.num || profile.num;
    return (
        <button
            type="button"
            className={`ticker-player-chip ${selected === key ? 'ticker-player-chip--active' : ''}`}
            onClick={() => onSelect(key)}
        >
            <span className="ticker-player-chip-num">{num || '–'}</span>
            <span className="ticker-player-chip-name">{profile.surname || '?'}</span>
        </button>
    );
}

function buildEventText({
    type,
    game,
    teamId,
    playerKey,
    playerOutKey,
    playerInKey,
    lineup,
    availablePlayers,
    currentFT,
}) {
    const homeId = toStr(game?.hid?._id || game?.hid);
    const homeName = game?.hid?.name || 'Heim';
    const awayName = game?.aid?.name || 'Gast';
    const teamName = teamId === homeId ? homeName : awayName;

    const nameForKey = (key) => {
        if (!key) return '?';
        const row = lineup.find((entry) => lineupPlayerKey(entry) === key);
        if (row) return playerDisplayNameFromEntry(row);
        const roster = availablePlayers?.find((p) => rosterEntryKey(p) === key);
        if (roster?.plid) return playerDisplayNameFromEntry({ plid: roster.plid });
        return '?';
    };

    if (type === 2) {
        const score = { ...(currentFT || { home: 0, away: 0 }) };
        return `TOR! ${teamName}${playerKey ? ` – ${nameForKey(playerKey)}` : ''}. Spielstand: ${score.home}:${score.away}`;
    }
    if (type === 3) {
        return `Gelbe Karte: ${playerKey ? nameForKey(playerKey) : teamName}`;
    }
    if (type === 4) {
        return `Rote Karte: ${playerKey ? nameForKey(playerKey) : teamName}`;
    }
    if (type === 5) {
        return `Wechsel ${teamName}: ${nameForKey(playerOutKey)} ⬇️ / ${nameForKey(playerInKey)} ⬆️`;
    }
    return '';
}

export function isStatTickerEvent(event) {
    return STAT_TYPES.has(event?.type);
}

export default function TickerEventEditPanel({
    event,
    game,
    lineup,
    events,
    availablePlayers,
    meta,
    onSaved,
    onCancel,
    onLineupChange,
}) {
    const homeId = toStr(game?.hid?._id || game?.hid);
    const awayId = toStr(game?.aid?._id || game?.aid);
    const homeName = game?.hid?.name || 'Heim';
    const awayName = game?.aid?.name || 'Gast';

    const [teamId, setTeamId] = useState(toStr(event.tid?._id || event.tid) || homeId);
    const [playerKey, setPlayerKey] = useState(getTickerEventPlayerKey(event));
    const [playerOutKey, setPlayerOutKey] = useState(getTickerEventPlayerOutKey(event));
    const [playerInKey, setPlayerInKey] = useState(getTickerEventPlayerInKey(event));
    const [minute, setMinute] = useState(event.minute || 0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const otherEvents = useMemo(
        () => (events || []).filter((entry) => entry._id !== event._id),
        [events, event._id]
    );

    const getNum = (key) => {
        const pt = availablePlayers?.find((p) => rosterEntryKey(p) === toStr(key));
        return pt?.num || '';
    };

    const teamPlayers = useMemo(() => {
        if (event.type === 5) {
            if (!playerOutKey) {
                return getOnFieldPlayers(lineup, teamId, getSubbedOutIds(otherEvents, teamId));
            }
            return getBenchPlayers(lineup, teamId, getSubbedInIds(otherEvents, teamId));
        }
        return getOnFieldPlayers(lineup, teamId, getSubbedOutIds(otherEvents, teamId))
            .concat(getBenchPlayers(lineup, teamId, getSubbedInIds(otherEvents, teamId)));
    }, [event.type, lineup, teamId, otherEvents, playerOutKey]);

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            let payload = {
                tid: teamId,
                minute: Number(minute) || 0,
            };

            if (event.type === 5) {
                if (!playerOutKey || !playerInKey || playerOutKey === playerInKey) {
                    setError('Bitte Spieler raus und rein wählen');
                    setSaving(false);
                    return;
                }
                payload = { ...payload, ...buildTickerSubPayload(playerOutKey, playerInKey) };
            } else if ([2, 3, 4].includes(event.type)) {
                payload = { ...payload, ...buildTickerPlayerPayload(playerKey || '') };
            }

            const text = buildEventText({
                type: event.type,
                game,
                teamId,
                playerKey,
                playerOutKey,
                playerInKey,
                lineup,
                availablePlayers,
                currentFT: meta?.scoreFT,
            });

            payload.textRaw = text;
            payload.textPublished = text;

            const res = await tickerAPI.update(event._id, payload);
            const saved = res.data?.data || { ...event, ...payload };

            if (event.type === 5 && onLineupChange) {
                const oldOut = getTickerEventPlayerOutKey(event);
                const oldIn = getTickerEventPlayerInKey(event);
                let nextLineup = lineup;
                if (oldOut && oldIn) {
                    nextLineup = revertSubstitutionFromLineup(nextLineup, oldOut, oldIn);
                }
                nextLineup = applySubstitutionToLineup(nextLineup, playerOutKey, playerInKey);
                onLineupChange(nextLineup);
            }

            onSaved(saved);
        } catch (err) {
            setError(err.response?.data?.message || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const subStep = event.type === 5 ? (playerOutKey ? 'in' : 'out') : null;

    return (
        <div className="ticker-event-edit">
            <p className="ticker-event-edit-label">
                {event.type === 5 ? 'Wechsel korrigieren' : 'Spielerzuordnung korrigieren'}
                <span className="ticker-event-edit-hint"> · wird in Team-Events synchronisiert</span>
            </p>

            <div className="ticker-event-edit-minute">
                <label>Minute</label>
                <input
                    type="number"
                    min="0"
                    max="120"
                    value={minute}
                    onChange={(e) => setMinute(e.target.value)}
                    className="ticker-event-edit-minute-input"
                />
            </div>

            <div className="ticker-inline-team-pick">
                <button type="button" className={`ticker-team-btn ${teamId === homeId ? 'ticker-team-btn--active' : ''}`} onClick={() => setTeamId(homeId)}>
                    🏠 {homeName}
                </button>
                <button type="button" className={`ticker-team-btn ${teamId === awayId ? 'ticker-team-btn--active' : ''}`} onClick={() => setTeamId(awayId)}>
                    ✈️ {awayName}
                </button>
            </div>

            {event.type === 5 ? (
                <>
                    {playerOutKey && (
                        <button type="button" className="ticker-clock-btn" onClick={() => { setPlayerOutKey(''); setPlayerInKey(''); }}>
                            ← Raus-Spieler ändern
                        </button>
                    )}
                    <p className="ticker-inline-step">
                        {subStep === 'out' ? 'Spieler raus' : 'Spieler rein'}
                    </p>
                    <div className="ticker-player-grid">
                        {teamPlayers.map((entry) => (
                            <PlayerChip
                                key={lineupPlayerKey(entry)}
                                entry={entry}
                                selected={subStep === 'out' ? playerOutKey : playerInKey}
                                onSelect={(key) => {
                                    if (subStep === 'out') setPlayerOutKey(key);
                                    else setPlayerInKey(key);
                                }}
                                getNum={getNum}
                            />
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <p className="ticker-inline-step">Spieler wählen</p>
                    <div className="ticker-player-grid">
                        {teamPlayers.map((entry) => (
                            <PlayerChip
                                key={lineupPlayerKey(entry)}
                                entry={entry}
                                selected={playerKey}
                                onSelect={setPlayerKey}
                                getNum={getNum}
                            />
                        ))}
                    </div>
                </>
            )}

            {error && <p className="ticker-event-edit-error">{error}</p>}

            <div className="ticker-event-edit-actions">
                <button type="button" className="ticker-clock-btn" onClick={onCancel}>Abbrechen</button>
                <button type="button" className="ticker-clock-btn ticker-clock-btn--go" onClick={handleSave} disabled={saving}>
                    {saving ? '…' : 'Speichern & sync'}
                </button>
            </div>
        </div>
    );
}
